import { ApiError } from '@/util/fetchClient';

/**
 * What the support-thread screens choose behavior and copy by. `network`
 * means no response arrived, so the request may or may not have landed; it is
 * the only kind a send is retried for, under the same message id, because
 * only an idempotent resend is safe when the outcome is unknown.
 */
export type ConversationErrorKind =
  | 'not_found'
  | 'invalid'
  | 'forbidden'
  | 'conflict'
  | 'provider_not_supported'
  | 'unavailable'
  | 'network'
  | 'unknown';

export class ConversationError extends Error {
  constructor(
    public kind: ConversationErrorKind,
    public status: number | undefined,
    message: string,
    /** The API's error `code`, such as `INVALID_REFERENCE`, when the response carried one. */
    public code?: string
  ) {
    super(message);

    this.name = 'ConversationError';
  }
}

function readCode(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null || !('code' in body)) {
    return undefined;
  }

  const { code } = body as { code?: unknown };

  return typeof code === 'string' ? code : undefined;
}

function kindFor(status: number, code: string | undefined): ConversationErrorKind {
  if (code === 'PROVIDER_NOT_SUPPORTED') {
    return 'provider_not_supported';
  }

  switch (status) {
    case 400:
      return 'invalid';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 409:
      return 'conflict';
    case 503:
      return 'unavailable';
    default:
      return 'unknown';
  }
}

/**
 * Maps anything a `fetchClient` call can throw to a `ConversationError`. A
 * `TypeError` is what `fetch` rejects with when no response arrives, which is
 * the one case treated as `network`.
 */
export function toConversationError(error: unknown): ConversationError {
  if (error instanceof ConversationError) {
    return error;
  }

  if (error instanceof ApiError) {
    const code = readCode(error.body);

    return new ConversationError(kindFor(error.status, code), error.status, error.message, code);
  }

  if (error instanceof TypeError) {
    return new ConversationError('network', undefined, error.message);
  }

  return new ConversationError(
    'unknown',
    undefined,
    error instanceof Error ? error.message : 'Unknown error'
  );
}
